import { DoubleQuotesIcon } from '@assets/icon';
import { ChatAgentType } from '@constants/chat-agents';
import React from 'react';
import ReactMarkdown from "react-markdown";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { materialDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import type { ChatMessage } from '../../../../types/chat-agent';
import { SenderType } from '../../../../types/chat-message';

interface QuotedMessagePreviewProps {
  quotedMessage: ChatMessage;
  onCancel: () => void;
  activeAgent: ChatAgentType;
}

const QuotedMessagePreview: React.FC<QuotedMessagePreviewProps> = ({
  quotedMessage,
  onCancel,
  activeAgent,
}) => {
  // Get sender name for display
  const getSenderName = () => {
    if (quotedMessage.user === SenderType.USER) {
      return 'Bạn';
    } else if (quotedMessage.agentType === ChatAgentType.CSKH) {
      return 'CSKH';
    } else {
      return 'Tendoo AI';
    }
  };

  // Extract text content, handle images
  const getPreviewText = () => {
    const text = quotedMessage.text || '';
    
    // Check if message is image-only
    const hasOnlyImages = text.trim().match(/^(\!\[image\]\(.*?\)\s*)+$/);
    if (hasOnlyImages) {
      return 'Hình ảnh';
    }

    if(quotedMessage.senderType === SenderType.BOT) {
      return (
        <div className="markdown-content-input-message">
          <ReactMarkdown
            rehypePlugins={[rehypeRaw]}
            remarkPlugins={[remarkGfm]}
            components={{
              code({ inline, className, children, ...props }: any) {
                const match = /language-(\w+)/.exec(className || "");
                return !inline && match ? (
                  <SyntaxHighlighter
                    {...props}
                    style={materialDark}
                    language={match[1]}
                    PreTag="div"
                  >
                    {String(children).replace(/\n$/, "")}
                  </SyntaxHighlighter>
                ) : (
                  <code className={className} {...props}>
                    {children}
                  </code>
                );
              },
            }}
          >
            {quotedMessage.text}
          </ReactMarkdown>
        </div>
      )
    }
    
    // Remove image markdown and get text only
    let textOnly = text.replace(/\!\[image\]\(.*?\)/g, '').trim();
    // Strip markdown syntax
    textOnly = textOnly
      .replace(/\*\*([^*]+)\*\*/g, '$1') // **bold**
      .replace(/\*([^*]+)\*/g, '$1')     // *italic*
      .replace(/__([^_]+)__/g, '$1')     // __bold__
      .replace(/_([^_]+)_/g, '$1')       // _italic_
      .replace(/\n/g, ' ')               // newlines to space
      .replace(/\s+/g, ' ')              // multiple spaces to single
      .trim();
    
    // Fallback: use suggestAgentNotify if text is empty
    return textOnly || quotedMessage.suggestAgentNotify || 'Hình ảnh';
  };

  return (
    <div className="quoted-message-preview bg-gray-100 p-2 mb-2 flex items-start justify-between gap-2 rounded-[14px_14px_2px_2px] p-[8px] gap-[16px]">
      <div className="flex-1 min-w-0">
        <div className="text-[12px] text-[var(--colour-primary-neutral-shade-sn)] font-normal mb-1">
          {"Đang trả lời "} <b>{getSenderName()}</b>
        </div>
        <div className="flex gap-1">
          <div>
            <div className="flex-shrink-0">
              <DoubleQuotesIcon />
            </div>
          </div>
          <div className="text-[12px] text-[var(--colour-primary-neutral-shade-sn)] line-clamp-3 break-words">
            {getPreviewText()}
          </div>
        </div>
      </div>
      <div className="h-full flex items-center gap-1 flex-shrink-0">
        <button
          onClick={onCancel}
          className="flex-shrink-0 text-gray-400 hover:text-gray-600 p-1"
          aria-label="Hủy trích dẫn"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>
    </div>
  );
};

export default QuotedMessagePreview;
